import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  RefreshCw, GitMerge, Search, Trash2, RotateCcw,
  CheckCircle, XCircle, Database, Table2, Layers, X,
} from 'lucide-react';
import '../../tailwindcss/Tailwind.css';
import ingestionService from './api';
import GraphViewer from './components/GraphViewer';
import NodeDetails from './components/NodeDetails';

const StatsBadge = ({ icon: Icon, label, value, color, active, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 rounded-lg border px-3 sm:px-4 py-2 sm:py-2.5 text-left transition-all shrink-0 ${color} ${
      onClick ? 'cursor-pointer hover:opacity-80' : 'cursor-default'
    } ${active ? 'ring-2 ring-offset-1 ring-current' : ''}`}
  >
    <Icon className="w-4 h-4 opacity-60 shrink-0" />
    <div className="min-w-0">
      <div className="text-base sm:text-lg font-bold leading-none">{value.toLocaleString()}</div>
      <div className="text-xs opacity-70 mt-0.5">{label}</div>
    </div>
  </button>
);

const KnowledgeGraph = () => {
  const [stats, setStats] = useState(null);
  const [domains, setDomains] = useState([]);
  const [rawGraph, setRawGraph] = useState(null);
  const [fgData, setFgData] = useState({ nodes: [], links: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [allSources, setAllSources] = useState([]);
  const [selectedSourceId, setSelectedSourceId] = useState('all');
  const [activeFilter, setActiveFilter] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionResult, setActionResult] = useState(null);
  const [search, setSearch] = useState('');

  const toFgData = useCallback((gNodes, gEdges) => ({
    nodes: gNodes.map((n) => ({ ...n })),
    links: gEdges.map((e) => ({ ...e, source: e.source, target: e.target })),
  }), []);

  const applyFilter = useCallback((graph, filter) => {
    const gNodes = graph.nodes || [];
    const gEdges = graph.edges || [];
    if (!filter) return toFgData(gNodes, gEdges);

    if (filter.kind === 'domain') {
      const dId = filter.domainId;
      const tablePaths = new Set(
        gEdges.filter((e) => e.type === 'BELONGS_TO' && e.target === dId).map((e) => e.source)
      );
      const sourceIds = new Set(
        gEdges.filter((e) => e.type === 'CONTAINS' && tablePaths.has(e.target)).map((e) => e.source)
      );
      const visible = new Set([dId, ...Array.from(tablePaths), ...Array.from(sourceIds)]);
      return toFgData(
        gNodes.filter((n) => visible.has(n.id)),
        gEdges.filter((e) => visible.has(e.source) && visible.has(e.target))
      );
    }

    if (filter.kind === 'type') {
      const visible = new Set(gNodes.filter((n) => n.type === filter.nodeType).map((n) => n.id));
      return toFgData(
        gNodes.filter((n) => visible.has(n.id)),
        gEdges.filter((e) => visible.has(e.source) && visible.has(e.target))
      );
    }

    return toFgData(gNodes, gEdges);
  }, [toFgData]);

  useEffect(() => {
    if (!rawGraph) return;
    setFgData(applyFilter(rawGraph, activeFilter));
  }, [activeFilter, rawGraph, applyFilter]);

  const toggleFilter = useCallback((next) => {
    setActiveFilter((prev) => {
      if (!prev || !next) return next;
      if (prev.kind === 'type'   && next.kind === 'type'   && prev.nodeType === next.nodeType)   return null;
      if (prev.kind === 'domain' && next.kind === 'domain' && prev.domainId === next.domainId)   return null;
      return next;
    });
  }, []);

  const loadGraph = useCallback(async (sourceId) => {
    setLoading(true);
    setError(null);
    const filter = sourceId ?? selectedSourceId;
    const ids = filter !== 'all' ? [filter] : undefined;

    try {
      const [graphData, statsData, domainsData] = await Promise.all([
        ingestionService.exploreGraph(ids),
        ingestionService.getGraphStats(),
        ingestionService.listGraphDomains(),
      ]);

      setRawGraph(graphData);
      setFgData(toFgData(graphData.nodes || [], graphData.edges || []));
      setStats(statsData);

      const rawDomains = (domainsData.domains || []).map((d) => ({
        id:          `domain_${d.name}`,
        name:         d.name,
        description:  d.description ?? '',
        table_count:  d.table_count ?? 0,
        parent_domain: d.parent_domain ?? '',
      }));
      setDomains(rawDomains);
      setActiveFilter(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load graph');
    } finally {
      setLoading(false);
    }
  }, [selectedSourceId, toFgData]);

  useEffect(() => { loadGraph(); }, [loadGraph]);

  useEffect(() => {
    ingestionService.getSources().then(setAllSources).catch(() => {});
  }, []);

  const handleDeleteGraph = useCallback(async () => {
    setActionLoading(true);
    setConfirmAction(null);
    setActionResult(null);
    try {
      const res = await ingestionService.clearGraph();
      setActionResult({
        ok: true,
        title: 'Graph deleted',
        body: `Removed ${res.deleted_nodes?.toLocaleString()} node(s) and ${res.deleted_relationships?.toLocaleString()} relationship(s).`,
      });
      await loadGraph();
    } catch (err) {
      setActionResult({ ok: false, title: 'Delete failed', body: err instanceof Error ? err.message : 'Unknown error' });
    } finally {
      setActionLoading(false);
    }
  }, [loadGraph]);

  const handleRebuildGraph = useCallback(async () => {
    setActionLoading(true);
    setConfirmAction(null);
    setActionResult(null);
    try {
      const res = await ingestionService.rebuildGraph();
      const warn = res.errors?.length > 0 ? ` (${res.errors.length} warning(s))` : '';
      setActionResult({
        ok: true,
        title: 'Graph rebuilt',
        body: `${res.sources_processed} source(s) · ${res.tables_written} table(s) · ${res.domains_written} domain link(s)${warn}`,
      });
      await loadGraph();
    } catch (err) {
      setActionResult({ ok: false, title: 'Rebuild failed', body: err instanceof Error ? err.message : 'Unknown error' });
    } finally {
      setActionLoading(false);
    }
  }, [loadGraph]);

  const handleRemoveNode = useCallback((nodeId) => {
    setFgData((prev) => ({
      nodes: prev.nodes.filter((n) => n.id !== nodeId),
      links: prev.links.filter((l) => {
        const ls = typeof l.source === 'object' ? l.source.id : l.source;
        const lt = typeof l.target === 'object' ? l.target.id : l.target;
        return ls !== nodeId && lt !== nodeId;
      }),
    }));
    setSelectedNode((prev) => (prev?.id === nodeId ? null : prev));
  }, []);

  const nodesWithHiddenNeighbors = useMemo(() => {
    if (!rawGraph) return new Set();
    const visibleIds = new Set(fgData.nodes.map((n) => n.id));
    const result = new Set();
    (rawGraph.edges || []).forEach((e) => {
      if (visibleIds.has(e.source) && !visibleIds.has(e.target)) result.add(e.source);
      if (visibleIds.has(e.target) && !visibleIds.has(e.source)) result.add(e.target);
    });
    return result;
  }, [rawGraph, fgData.nodes]);

  const nc = stats?.node_counts ?? {};
  const ec = stats?.edge_counts ?? {};

  return (
    <div className="flex flex-col bg-gray-50 min-h-[calc(100dvh-12rem)] md:h-[calc(100dvh-12rem)] md:overflow-hidden">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-3 sm:px-6 pt-6 pb-3 flex flex-col sm:flex-row sm:items-center gap-3 shrink-0">
        <div className="min-w-0 w-full sm:w-auto"></div>

        {/* Source filter */}
        <select
          value={selectedSourceId}
          onChange={(e) => { setSelectedSourceId(e.target.value); loadGraph(e.target.value); }}
          className="w-full sm:w-auto max-w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
        >
          <option value="all">All Sources</option>
          {allSources.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>

        {/* Search */}
        <div className="relative w-full sm:w-48 min-w-0">
          <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search nodes..."
            className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-2 text-gray-400 hover:text-gray-600">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:ml-auto">
          <button
            onClick={() => loadGraph()}
            disabled={actionLoading}
            className="flex items-center text-primary text-sm bg-transparent border-none cursor-pointer py-1 px-2 rounded transition-all duration-200 hover:bg-primaryLight disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <RefreshCw size={16} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setConfirmAction('rebuild')}
            disabled={actionLoading}
            className="flex items-center text-primaryDark text-sm bg-transparent border-none cursor-pointer py-1 px-2 rounded transition-all duration-200 hover:bg-primaryLight disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <RotateCcw size={16} className="mr-1" />
            Rebuild
          </button>
          <button
            onClick={() => setConfirmAction('delete')}
            disabled={actionLoading}
            className="flex items-center text-red-600 text-sm bg-transparent border-none cursor-pointer py-1 px-2 rounded transition-all duration-200 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <Trash2 size={16} className="mr-1" />
            Delete
          </button>
        </div>
      </div>

      {/* Stats bar */}
      {stats?.available && (
        <div className="bg-white border-b border-gray-200 px-3 sm:px-6 py-2.5 flex items-center gap-2 shrink-0 overflow-x-auto">
          {activeFilter && (
            <button
              onClick={() => setActiveFilter(null)}
              className="text-xs text-gray-500 hover:text-gray-800 border border-gray-300 rounded-lg px-2 py-1 shrink-0 mr-1"
            >
              Clear filter
            </button>
          )}
          <StatsBadge icon={Database} label="Sources" value={nc.Source ?? 0}
            color="bg-blue-50 border-blue-200 text-primaryDark"
            active={activeFilter?.kind === 'type' && activeFilter.nodeType === 'source'}
            onClick={() => toggleFilter({ kind: 'type', nodeType: 'source' })} />
          <StatsBadge icon={Table2} label="Tables" value={nc.Table ?? 0}
            color="bg-blue-50 border-blue-200 text-primaryDark"
            active={activeFilter?.kind === 'type' && activeFilter.nodeType === 'table'}
            onClick={() => toggleFilter({ kind: 'type', nodeType: 'table' })} />
          <StatsBadge icon={Layers} label="Domains" value={nc.Domain ?? 0}
            color="bg-green-50 border-green-200 text-green-800"
            active={activeFilter?.kind === 'type' && activeFilter.nodeType === 'domain'}
            onClick={() => toggleFilter({ kind: 'type', nodeType: 'domain' })} />

          <div className="ml-0 sm:ml-auto flex items-center gap-3 sm:gap-4 text-xs text-gray-400 pr-1 shrink-0">
            <span><span className="font-semibold text-primaryDark">{ec.CONTAINS ?? 0}</span> contains</span>
            <span><span className="font-semibold text-emerald-600">{ec.BELONGS_TO ?? 0}</span> belongs to</span>
          </div>
        </div>
      )}

      {/* Confirm modal */}
      {confirmAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className={`px-6 py-4 flex items-center gap-3 ${
              confirmAction === 'delete' ? 'bg-red-50 border-b border-red-200' : 'bg-blue-50 border-b border-blue-200'
            }`}>
              {confirmAction === 'delete'
                ? <Trash2 className="w-5 h-5 text-red-600 shrink-0" />
                : <RotateCcw className="w-5 h-5 text-primaryDark shrink-0" />}
              <h2 className={`font-bold text-lg ${confirmAction === 'delete' ? 'text-red-800' : 'text-primaryDark'}`}>
                {confirmAction === 'delete' ? 'Delete Knowledge Graph?' : 'Rebuild Knowledge Graph?'}
              </h2>
            </div>
            <div className="px-6 py-5 text-gray-700 text-sm">
              {confirmAction === 'delete'
                ? 'This will permanently delete all nodes and relationships from Neo4j. This cannot be undone.'
                : 'Clears the current graph, then replays all stored analysis results back into Neo4j.'}
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => setConfirmAction(null)} className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-100">
                Cancel
              </button>
              {confirmAction === 'delete' ? (
                <button onClick={handleDeleteGraph} className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700">
                  Yes, Delete Everything
                </button>
              ) : (
                <button onClick={handleRebuildGraph} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">
                  Yes, Rebuild
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Action loading overlay */}
      {actionLoading && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-2xl shadow-xl px-8 py-6 flex items-center gap-4">
            <RefreshCw className="w-6 h-6 text-primaryDark animate-spin" />
            <div>
              <div className="font-semibold text-gray-800">Working...</div>
              <div className="text-sm text-gray-500 mt-0.5">This may take up to a minute</div>
            </div>
          </div>
        </div>
      )}

      {/* Result toast */}
      {actionResult && (
        <div className="fixed bottom-4 left-2 right-2 sm:bottom-6 sm:right-6 sm:left-auto z-50 sm:max-w-sm">
          <div className={`rounded-xl border shadow-lg px-5 py-4 flex items-start gap-3 ${
            actionResult.ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            {actionResult.ok
              ? <CheckCircle className="w-5 h-5 shrink-0 mt-0.5 text-green-600" />
              : <XCircle    className="w-5 h-5 shrink-0 mt-0.5 text-red-500" />}
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">{actionResult.title}</div>
              <div className="text-xs mt-0.5 leading-relaxed">{actionResult.body}</div>
            </div>
            <button onClick={() => setActionResult(null)} className="shrink-0 text-lg leading-none opacity-50 hover:opacity-100">×</button>
          </div>
        </div>
      )}

      {/* Main area */}
      <div className="flex flex-1 min-h-0 flex-col md:flex-row overflow-y-auto md:overflow-hidden overscroll-contain bg-gray-50">
        {/* Left: domains sidebar */}
        <aside className="w-full md:w-56 bg-white border-b border-l md:border-b-0 md:border-r border-gray-200 flex flex-col shrink-0 overflow-hidden md:max-h-none max-h-52 ml-6 sm:ml-9">
          <div className="px-4 py-3 border-b border-gray-100 shrink-0">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Business Domains</h2>
          </div>
          <div className="flex-1 overflow-y-auto">
            {domains.length === 0 ? (
              <div className="px-4 py-6 text-xs text-gray-400 text-center">
                No domains yet — run domain analysis first
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {domains.map((d) => {
                  const isActive = activeFilter?.kind === 'domain' && activeFilter.domainId === d.id;
                  return (
                    <li
                      key={d.id}
                      onClick={() => toggleFilter({ kind: 'domain', domainId: d.id, label: d.name })}
                      className={`px-4 py-2.5 cursor-pointer transition-colors ${
                        isActive ? 'bg-green-50 border-l-2 border-green-500' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-1">
                        <span className={`text-sm font-medium truncate ${isActive ? 'text-green-800' : 'text-gray-800'}`}>
                          {d.name}
                        </span>
                        <span className={`text-xs rounded-full px-1.5 py-0.5 shrink-0 ${
                          d.table_count > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
                        }`}>
                          {d.table_count}
                        </span>
                      </div>
                      {d.description && (
                        <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{d.description}</p>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>

        {/* Graph canvas */}
        <GraphViewer
          fgData={fgData}
          rawGraph={rawGraph}
          loading={loading}
          error={error}
          selectedNode={selectedNode}
          onNodeClick={(node) => {
            const gn = (rawGraph?.nodes ?? []).find((n) => n.id === node.id) ?? null;
            setSelectedNode(gn);
          }}
          onBackgroundClick={() => setSelectedNode(null)}
          onNodeRemove={handleRemoveNode}
          onRetry={loadGraph}
          search={search}
          nodesWithHiddenNeighbors={nodesWithHiddenNeighbors}
        />

        {/* Node detail panel */}
        {selectedNode && (
          <NodeDetails
            node={selectedNode}
            domains={domains}
            onClose={() => setSelectedNode(null)}
            onRemove={handleRemoveNode}
          />
        )}
      </div>
    </div>
  );
};

export default KnowledgeGraph;
